import { useState } from 'react'
import type { JSX } from 'react'
import { parseAsString, useQueryState } from 'nuqs'

import { clsx } from 'clsx'

import {
  SettingsIcon,
  RefreshCwIcon,
  Skeleton,
  Button,
  HeartIcon,
} from '@buffer-mono/popcorn'

import { useIdeaComposer } from '~publish/components/IdeaManagementRouter'
import { Credits } from '~publish/legacy/ai/components/GenerateIdeasButton/Credits'
import { ErrorMessage } from '~publish/legacy/ai/components/GenerateIdeasButton/ErrorMessage'
import { getPrompt } from '~publish/legacy/ai/helpers/localStorage'
import { selectGenerateContentLimits } from '~publish/legacy/ai/state/aiLimits/slice'
import {
  selectGeneratedIdeas,
  clearIdeas,
} from '~publish/legacy/ai/state/contentIdeation/slice'
import { generateIdeas } from '~publish/legacy/ai/state/contentIdeation/thunks'
import { useAppSelector, useAppDispatch } from '~publish/legacy/store'
import { VIEWS } from '~publish/pages/Create/IdeasPage'

import styles from './GeneratedIdea.module.css'

const LoadingState = (): JSX.Element => (
  <div className={styles.skeletonWrapper}>
    <Skeleton width="80%" height={12} rounded />
    <Skeleton width="90%" height={12} rounded />
    <Skeleton width="83%" height={12} rounded />
    <Skeleton width="50%" height={12} rounded />
    <Skeleton width="80%" height={12} rounded />
  </div>
)

/**
 *
 */
export const GeneratedIdea = ({
  onUpgrade,
}: {
  onUpgrade: () => void
}): JSX.Element | null => {
  const [animateButton, setAnimateButton] = useState(false)
  const { createIdeaWithComposer } = useIdeaComposer()
  const [view] = useQueryState('view', parseAsString)

  const dispatch = useAppDispatch()

  const { canGenerateContent, generateContentTokensLeft, limitsLoaded } =
    useAppSelector((state) => selectGenerateContentLimits(state))
  const showCredits = limitsLoaded && generateContentTokensLeft === 0

  const { generatedIdeas, generateIdeasLoading, generateIdeasError } =
    useAppSelector((state) => selectGeneratedIdeas(state))

  const onTryAnother = (): void => {
    if (canGenerateContent) {
      dispatch(
        generateIdeas({
          prompt: getPrompt(),
          cta: 'publish-ideas-contentIdeationWidget-tryAnother-1',
        }),
      )
    } else {
      setAnimateButton(true)
      setTimeout(() => setAnimateButton(false), 500)
    }
  }

  const onUseIt = (): void => {
    const idea = generatedIdeas?.[0]
    if (!idea) return
    createIdeaWithComposer({
      text: idea,
      aiAssisted: 'true',
      source:
        view === VIEWS.GALLERY
          ? 'create-gallery-ideaGenerator-newIdea-1'
          : view === VIEWS.LIST
            ? 'create-list-ideaGenerator-newIdea-1'
            : 'create-board-ideaGenerator-newIdea-1',
    })
  }

  const onCustomize = (): void => {
    if (canGenerateContent) {
      dispatch(clearIdeas(undefined))
    } else {
      onUpgrade()
    }
  }

  if (!generatedIdeas) return null

  return (
    <div className={styles.wrapper}>
      <button className={styles.customizeButton} onClick={onCustomize}>
        <SettingsIcon />
        Customize
      </button>
      <div className={styles.content}>
        {generateIdeasLoading ? (
          <LoadingState />
        ) : (
          <p
            className={clsx(
              styles.idea,
              generateIdeasLoading && styles.loading,
            )}
            translate="no"
          >
            {generatedIdeas[0]}
          </p>
        )}
        <div className={styles.buttons}>
          {generateIdeasLoading ? (
            <p className={styles.loadingMessage}>
              <RefreshCwIcon />
              Generating another idea...
            </p>
          ) : (
            <>
              <Button
                variant="tertiary"
                size="large"
                className={styles.button}
                onClick={onTryAnother}
              >
                <RefreshCwIcon />
                Try Another
              </Button>
              <div className={styles.divider} />
              <Button
                variant="tertiary"
                size="large"
                className={clsx(styles.button, styles.buttonPrimary)}
                onClick={onUseIt}
              >
                <HeartIcon />
                Use It
              </Button>
            </>
          )}
        </div>
      </div>
      {generateIdeasError && <ErrorMessage message={generateIdeasError} />}
      {showCredits && (
        <div
          className={clsx(
            styles.errorMessageContainer,
            animateButton && styles.animate,
          )}
        >
          <Credits />
        </div>
      )}
    </div>
  )
}
